import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Label } from '@rudi/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, QueryState } from '../components/page';
import { DomainChipsInput, useDomainDraft } from '../components/DomainChipsInput';
import {
  useDeleteTenant,
  useDemoteMember,
  usePromoteMember,
  useTenant,
  useTenantDeletionImpact,
  useRenameTenant,
  useTenantMembers,
  useUpdateTenantDomains,
} from '../api/hooks';
import { TenantVoiceWritingSection } from './TenantVoiceWritingSection';
import { TenantTeamsSection } from './TenantTeamsSection';
import type { AdminTenant } from '../api/types';
import { ChatExecutorSection } from './ChatExecutorSection';
import { TenantFeaturesSection } from './TenantFeaturesSection';
import { TenantPatientModelSection } from './PatientModelSettings';

/** The backend's reason for a rejected write, falling back to the transport error. */
function errorMessage(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: { error?: string; detail?: string } } })?.response?.data;
  return data?.error ?? data?.detail ?? (e instanceof Error ? e.message : fallback);
}

function ConfigSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3 rounded-3xl border p-4">
      <div>
        <h2 className="text-sm font-medium">{title}</h2>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

function AllowedDomainsSection({ tenant }: { tenant: AdminTenant }) {
  const mutation = useUpdateTenantDomains(tenant.id);
  const editor = useDomainDraft(tenant.allowed_email_domains);

  const save = async () => {
    const domains = editor.prepareSave();
    if (domains === null) return;
    try {
      await mutation.mutateAsync(domains);
      editor.reset();
      toast.success('Allowed domains updated');
    } catch (e) {
      toast.error(errorMessage(e, 'Update failed'));
    }
  };

  return (
    <ConfigSection
      title="Allowed email domains"
      description={`Users signing up with these domains join ${tenant.name}. Save your changes after adding or removing domains.`}
    >
      <DomainChipsInput editor={editor} disabled={mutation.isPending} hideLabel />
      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={mutation.isPending || !editor.hasChanges}
          onClick={() => void save()}
        >
          {mutation.isPending ? 'Saving…' : 'Save domains'}
        </Button>
      </div>
    </ConfigSection>
  );
}

function RenameTenantButton({ tenant }: { tenant: AdminTenant }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(tenant.name);
  const mutation = useRenameTenant(tenant.id);
  const save = async () => {
    try {
      await mutation.mutateAsync(name.trim());
      setOpen(false);
      toast.success('Tenant renamed');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not rename tenant'));
    }
  };
  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          setName(tenant.name);
          setOpen(true);
        }}
      >
        Rename
      </Button>
      <Dialog
        open={open}
        onOpenChange={value => {
          if (!mutation.isPending) setOpen(value);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename tenant</DialogTitle>
            <DialogDescription>
              Choose the organization name shown in Cortex and Rudi.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={event => {
              event.preventDefault();
              if (name.trim() && name.trim() !== tenant.name && !mutation.isPending) void save();
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="tenant-name">Tenant name</Label>
              <Input
                id="tenant-name"
                value={name}
                maxLength={255}
                disabled={mutation.isPending}
                onChange={event => setName(event.target.value)}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={mutation.isPending}
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={mutation.isPending || !name.trim() || name.trim() === tenant.name}
              >
                {mutation.isPending ? 'Saving…' : 'Save name'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function DeleteTenantButton({ tenant }: { tenant: AdminTenant }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const navigate = useNavigate();
  const impact = useTenantDeletionImpact(tenant.id, open);
  const mutation = useDeleteTenant(tenant.id);
  const leaving = impact.data?.members.filter(member => member.otherTenants === 0) ?? [];
  const confirmed = typed.trim() === tenant.name;
  const remove = async () => {
    try {
      await mutation.mutateAsync();
      setOpen(false);
      toast.success(`${tenant.name} deleted`);
      void navigate({ to: '/tenants' });
    } catch (error) {
      toast.error(errorMessage(error, 'Could not delete tenant'));
    }
  };
  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          setTyped('');
          setOpen(true);
        }}
      >
        Delete
      </Button>
      <Dialog
        open={open}
        onOpenChange={value => {
          if (!mutation.isPending) setOpen(value);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete tenant</DialogTitle>
            <DialogDescription>
              Deletes {tenant.name} with its roles, teams, memberships and invitations. Its audit
              history stays. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <QueryState isLoading={impact.isLoading} error={impact.error}>
            {impact.data?.blocked ? (
              <div role="alert" className="space-y-2 text-sm">
                {impact.data.blockers.map(blocker => (
                  <p key={blocker}>{blocker}</p>
                ))}
              </div>
            ) : (
              impact.data && (
                <form
                  onSubmit={event => {
                    event.preventDefault();
                    if (confirmed && !mutation.isPending) void remove();
                  }}
                  className="space-y-4"
                >
                  <p className="text-sm">
                    {impact.data.teamCount} {impact.data.teamCount === 1 ? 'team' : 'teams'} and{' '}
                    {impact.data.members.length}{' '}
                    {impact.data.members.length === 1 ? 'membership' : 'memberships'} go with it.
                  </p>
                  {leaving.length > 0 && (
                    <div className="space-y-1 text-sm">
                      <p>These people belong to no other tenant and can no longer sign in:</p>
                      <ul className="list-disc pl-5">
                        {leaving.map(member => (
                          <li key={member.email ?? member.displayName}>
                            {member.displayName && member.displayName !== member.email
                              ? `${member.displayName} (${member.email})`
                              : member.email}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="tenant-delete-confirm">Type {tenant.name} to confirm</Label>
                    <Input
                      id="tenant-delete-confirm"
                      value={typed}
                      autoComplete="off"
                      disabled={mutation.isPending}
                      onChange={event => setTyped(event.target.value)}
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={mutation.isPending}
                      onClick={() => setOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="destructive"
                      disabled={mutation.isPending || !confirmed}
                    >
                      {mutation.isPending ? 'Deleting…' : 'Delete tenant'}
                    </Button>
                  </DialogFooter>
                </form>
              )
            )}
          </QueryState>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function TenantDetailPage() {
  const { tenantId } = useParams({ from: '/tenants/$tenantId' });
  const tenant = useTenant(tenantId);
  const { data, isLoading, error } = useTenantMembers(tenantId);
  const promote = usePromoteMember(tenantId);
  const demote = useDemoteMember(tenantId);
  const onPromote = async (userId: string) => {
    try {
      await promote.mutateAsync(userId);
      toast.success('Promoted to tenant admin');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Promote failed');
    }
  };

  const onDemote = async (userId: string) => {
    try {
      await demote.mutateAsync(userId);
      toast.success('Tenant admin assignment removed');
    } catch (e) {
      // The backend returns 409 when demoting the last admin.
      toast.error(errorMessage(e, 'Demote failed'));
    }
  };

  return (
    <div>
      <Link
        to="/tenants"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        Tenants
      </Link>
      <PageHeader
        title={tenant.data?.name ?? data?.tenant_name ?? 'Tenant'}
        description="Configure this organization and promote a member to tenant admin."
        actions={
          tenant.data && (
            <div className="flex gap-2">
              <RenameTenantButton key={tenantId} tenant={tenant.data} />
              <DeleteTenantButton key={`delete-${tenantId}`} tenant={tenant.data} />
            </div>
          )
        }
      />

      <div className="mb-6">
        <QueryState isLoading={tenant.isLoading} error={tenant.error}>
          {tenant.data && (
            <div className="grid gap-4 md:grid-cols-2">
              <ChatExecutorSection key={`chat-${tenantId}`} tenant={tenant.data} />
              <AllowedDomainsSection key={`domains-${tenantId}`} tenant={tenant.data} />
            </div>
          )}
        </QueryState>
      </div>

      <QueryState isLoading={isLoading} error={error} empty={data?.members.length === 0}>
        <div className="rounded-3xl border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.members.map(m => (
                <TableRow key={m.user_id}>
                  <TableCell className="font-medium">
                    {m.display_name ?? m.username}
                    {m.is_super_admin && (
                      <Badge variant="secondary" className="ml-2">
                        super-admin
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{m.email ?? '—'}</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <p>{(m.role_display_names ?? [m.role_display_name]).join(', ')}</p>
                      <Link
                        className="text-sm underline"
                        to="/tenants/$tenantId/access"
                        params={{ tenantId }}
                      >
                        Manage roles
                      </Link>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(m.joined_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {m.is_tenant_admin ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={demote.isPending}
                        onClick={() => onDemote(m.user_id)}
                      >
                        Demote
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={promote.isPending}
                        onClick={() => onPromote(m.user_id)}
                      >
                        Make admin
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </QueryState>
      <Link
        to="/tenants/$tenantId/access"
        params={{ tenantId }}
        className="inline-flex rounded-3xl border px-4 py-2"
      >
        Roles and permissions
      </Link>
      <TenantFeaturesSection key={`features-${tenantId}`} tenantId={tenantId} />
      <TenantVoiceWritingSection key={`voice-${tenantId}`} tenantId={tenantId} />
      <TenantPatientModelSection
        key={`patient-model-${tenantId}`}
        tenantId={tenantId}
        tenantName={tenant.data?.name}
      />
      <TenantTeamsSection key={tenantId} tenantId={tenantId} />
    </div>
  );
}
